import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  usePreOrderMutations,
  usePreOrderFlowMutations,
  useAISync,
} from "@stockline/web";
import { useUser } from "@/components/auth-provider";
import type { PreOrder, PreOrderMetadata } from "@/app/api/types/preOrder";
import { useRecapBasePath } from "../contexts/RecapContext";
import { preOrderListSnapshot } from "../stores/preOrderListSnapshotStore";

interface SplitOrderResult {
  newOrderId: string;
}

/**
 * Strip lifecycle-specific fields from metadata so the cloned order starts
 * clean (no stale export state, sync health, or AI suggestion blocks).
 */
function cloneMetadataForSplit(
  metadata: PreOrderMetadata | null | undefined,
): Record<string, unknown> | null {
  if (!metadata) return null;
  const {
    export: _export,
    as400_export: _as400,
    sync_health: _sync,
    ai_suggestions: _aiSuggestions,
    ai_suggestion: _aiSuggestionLegacy,
    seen: _seen,
    ...rest
  } = metadata as Record<string, unknown>;
  if (Object.keys(rest).length === 0) return null;
  return rest;
}

/**
 * Splits selected flows out of an existing pre-order into a brand-new one.
 *
 * Offline-first: writes to Dexie immediately (no server round-trip), then
 * enqueues changes for background sync. The new order clones the original's
 * header fields (partner, dates, metadata, category, type, etc.) and receives
 * the selected flows; the original keeps the rest.
 *
 * After split:
 *  - The new order is inserted into the snapshot nav list right after the original.
 *  - The focus view navigates to the new order.
 *  - A persistent toast offers a button to go back to the original.
 */
export function useSplitOrder() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const basePath = useRecapBasePath();
  const viewer = useUser();
  const { createPreOrder } = usePreOrderMutations();
  const { updateFlow } = usePreOrderFlowMutations();
  const { engine } = useAISync();

  const splitOrder = useCallback(
    async (
      originalOrder: PreOrder,
      selectedFlowIds: string[],
    ): Promise<SplitOrderResult> => {
      if (selectedFlowIds.length === 0) {
        throw new Error("No flows selected for split");
      }

      const newOrderId = crypto.randomUUID();
      const now = new Date().toISOString();

      // Clone header fields from the original order.
      await createPreOrder({
        id: newOrderId,
        companyId: viewer.company_id ?? "",
        userId: viewer.id,
        employeeId: originalOrder.employee_id ?? null,
        partnerId: originalOrder.partner_id ?? null,
        shippingAddressId: originalOrder.shipping_address_id ?? null,
        locationId: originalOrder.location_id ?? null,
        routeId: originalOrder.route_id ?? null,
        groupId: originalOrder.group_id ?? null,
        buyerId: originalOrder.buyer_id ?? null,
        sourceType: 3, // MANUAL
        status: 0, // PENDING
        type: originalOrder.type ?? 0,
        category: originalOrder.category ?? 0,
        deliveryDate: originalOrder.delivery_date ?? null,
        orderDate: now.slice(0, -1), // Strip trailing Z for consistency
        // preOrderNumber is intentionally NOT copied: the partial unique
        // index uq_pre_orders_company_erp_number enforces uniqueness on
        // (company_id, pre_order_number) for source_type=3 live rows.
        customerPoNumber: originalOrder.customer_po_number ?? null,
        comment: originalOrder.comment ?? null,
        preOrderMetadata: cloneMetadataForSplit(
          originalOrder.pre_order_metadata,
        ),
        sharedWithUserIds: originalOrder.shared_with_user_ids ?? [],
      });

      // Flush the new PO to the server before reparenting flows.
      // pre_order_flows.pre_order_id has a FK to pre_orders.id, so the
      // flow updates will be rejected if the new PO hasn't landed yet.
      await engine.pushChanges("pre-orders");

      // Move selected flows to the new order by updating their preOrderId.
      await Promise.all(
        selectedFlowIds.map((flowId) =>
          updateFlow(flowId, { preOrderId: newOrderId }),
        ),
      );

      // Each updateFlow fires `void engine.pushChanges("pre-order-flows")`,
      // but that's fire-and-forget. Await the drain so the reparent push
      // completes before navigation (avoids flaky sync timing in tests and
      // ensures the server state is consistent before the user sees the new PO).
      await engine.pushChanges("pre-order-flows");

      // Insert the new order into the snapshot nav list right after the original.
      const currentIds = preOrderListSnapshot.getIds();
      const originalIndex = currentIds.indexOf(originalOrder.id);
      if (originalIndex >= 0) {
        const updatedIds = [...currentIds];
        updatedIds.splice(originalIndex + 1, 0, newOrderId);
        preOrderListSnapshot.setIds(updatedIds);
      }

      // Navigate to the new order.
      navigate(`${basePath}/${newOrderId}`);

      // Persistent toast with button to go back to the original order.
      toast.success(
        t("ordersRecap.flowBatch.splitSuccess", "Order split successfully"),
        {
          duration: Infinity,
          action: {
            label: t(
              "ordersRecap.flowBatch.goToOriginal",
              "Go to original order",
            ),
            onClick: () => navigate(`${basePath}/${originalOrder.id}`),
          },
        },
      );

      return { newOrderId };
    },
    [createPreOrder, updateFlow, engine, navigate, basePath, viewer, t],
  );

  return { splitOrder };
}
